<script setup lang="ts">
import { ref, computed, watch } from 'vue';
import { useTranslation } from '@/utils/locales/messages';
import { INDIVIDUAL_SYSTEM_COLUMNS, CORPORATE_SYSTEM_COLUMNS } from '../types/contact.types';

interface Props {
    contactType: 'Individual' | 'Corporate' | '';
    file: File | null;
}

const props = withDefaults(defineProps<Props>(), {
    contactType: '',
    file: null
});

const emit = defineEmits<{
    'update:contactType': [value: 'Individual' | 'Corporate'];
    'update:file': [value: File | null];
    'file-uploaded': [data: { columns: string[], rows: any[] }];
}>();

const { createSyncT } = useTranslation();
const currentLanguage = ref(localStorage.getItem('preferred-language') || 'en');
const t = createSyncT(currentLanguage);

const localContactType = computed({
    get: () => props.contactType || 'Individual',
    set: (value) => emit('update:contactType', value)
});

const localFile = ref<File | null>(props.file);
const isDragging = ref(false);
const uploadError = ref<string>('');

// 시스템 컬럼에서 label만 추출하여 헤더로 사용
const getColumnHeaders = (columns: typeof INDIVIDUAL_SYSTEM_COLUMNS) => {
    return columns.map(col => col.label);
};

// 템플릿 파일 생성 (exceljs 사용)
const generateTemplate = async (type: 'Individual' | 'Corporate') => {
    try {
        const ExcelJS = await import('exceljs');
        const workbook = new ExcelJS.Workbook();
        const worksheet = workbook.addWorksheet('Contact Data');
        
        const systemColumns = type === 'Individual' ? INDIVIDUAL_SYSTEM_COLUMNS : CORPORATE_SYSTEM_COLUMNS;
        const headers = getColumnHeaders(systemColumns);
        
        // 샘플 데이터 매핑 (컬럼 key 기반) - 타입별로 분리
        const individualSampleData: Record<string, string> = {
            'ID_TYPE_CD': 'NRIC(Citizen)',
            'CONTACT_REG_NO': 'S1234567A',
            'NATIONALITY_CD': 'SG',
            'DATE_OF_BIRTH': '09/11/1982', // MM/DD/YYYY 형식
            'FIRST_NAME': 'John',
            'MIDDLE_NAME': 'Michael',
            'SURNAME': 'Doe',
            'COUNTRY_CD': '+65',
            'AREA_CODE': '02',
            'MOBILE_NO': '91234567',
            'CONTACT_EMAIL': 'john.doe@example.com',
            'CONTACT_EMAIL_2': 'john.doe2@example.com',
            'NOTE': 'Sample note',
        };
        
        const corporateSampleData: Record<string, string> = {
            'JURISDICTION': 'SG',
            'AUDITOR_YN': 'No',
            'CONTACT_NAME': 'ABC Company Pte Ltd',
            'CONTACT_REG_NO': '201234567D',
            'DATE_OF_INCORPORATION': '09/11/1982', // MM/DD/YYYY 형식
            'COUNTRY_CD': '+65',
            'AREA_CODE': '02',
            'MOBILE_NO': '91234567',
            'CONTACT_EMAIL': 'info@abccompany.com',
            'CONTACT_EMAIL_2': 'contact@abccompany.com',
            'NOTE': 'Sample note',
            // Representative Name은 File Upload에서 제외
        };
        
        // 타입에 따라 적절한 샘플 데이터 선택
        const sampleDataMap = type === 'Individual' ? individualSampleData : corporateSampleData;
        
        const sampleRow = systemColumns.map(col => sampleDataMap[col.key] || '');

        worksheet.addRow(headers);
        worksheet.addRow(sampleRow);
        headers.forEach((_, index) => {
            const column = worksheet.getColumn(index + 1);
            column.width = 25;
        });

        const fileName = type === 'Individual' 
            ? 'Individual_contact_upload_form.xlsx'
            : 'Corporate_upload_form.xlsx';

        const buffer = await workbook.xlsx.writeBuffer();
        const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = fileName;
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (error: any) {
        console.error('템플릿 생성 실패:', error);
        alert('템플릿 생성에 실패했습니다. exceljs 라이브러리가 설치되어 있는지 확인해주세요.');
    }
};

const generateIndividualTemplate = () => {
    if (!localContactType.value || localContactType.value !== 'Individual') {
        alert('Please select Individual contact type first.');
        return;
    }
    generateTemplate('Individual');
};

const generateCorporateTemplate = () => {
    if (!localContactType.value || localContactType.value !== 'Corporate') {
        alert('Please select Corporate contact type first.');
        return;
    }
    generateTemplate('Corporate');
};

// ZIP 파일 내부 검증
const validateZipFile = async (file: File): Promise<boolean> => {
    try {
        // jszip 라이브러리 동적 import
        const JSZip = (await import('jszip')).default;
        const zip = await JSZip.loadAsync(file);
        
        const fileNames = Object.keys(zip.files);
        const excelFiles = fileNames.filter(name => 
            name.toLowerCase().endsWith('.xlsx')
        );
        
        if (excelFiles.length === 0) {
            uploadError.value = 'ZIP file must contain at least one Excel file (.xlsx)';
            return false;
        }
        
        // ZIP 파일 내부에 비-Excel 파일이 있는지 확인
        const nonExcelFiles = fileNames.filter(name => {
            const lowerName = name.toLowerCase();
            return !lowerName.endsWith('.xlsx') && 
                   !name.endsWith('/') && // 디렉토리는 제외
                   name.trim() !== '';
        });
        
        if (nonExcelFiles.length > 0) {
            uploadError.value = 'ZIP file contains non-Excel files. Only .xlsx files are allowed.';
            return false;
        }
        
        return true;
    } catch (error: any) {
        console.error('ZIP 파일 검증 실패:', error);
        uploadError.value = 'Failed to validate ZIP file. Please ensure it is a valid ZIP archive.';
        return false;
    }
};

// Excel 파일 파싱 (exceljs 사용)
const parseExcelFile = async (file: File): Promise<{ columns: string[], rows: any[] }> => {
    try {
        const ExcelJS = await import('exceljs');
        const workbook = new ExcelJS.Workbook();
        const data = await file.arrayBuffer();
        await workbook.xlsx.load(data);

        const worksheet = workbook.worksheets[0];
        if (!worksheet) {
            throw new Error('Excel file is empty');
        }

        const columnCount = worksheet.actualColumnCount || worksheet.columnCount || 0;
        const jsonData: string[][] = [];

        worksheet.eachRow({ includeEmpty: true }, (row) => {
            const rowValues: string[] = [];
            for (let colIndex = 1; colIndex <= columnCount; colIndex++) {
                const cell = row.getCell(colIndex);
                let cellValue = '';
                
                if (cell) {
                    // cell.text를 최우선으로 사용 (ExcelJS가 대부분의 경우 올바른 문자열을 반환)
                    if (cell.text !== undefined && cell.text !== null) {
                        // 공백 문자열도 유효한 값으로 처리 (trim() 체크 제거)
                        if (typeof cell.text === 'string') {
                            cellValue = cell.text;
                        } else if (typeof cell.text === 'object' && 'text' in cell.text) {
                            // cell.text가 객체인 경우 (RichText 등)
                            const textValue = (cell.text as any).text;
                            if (typeof textValue === 'string') {
                                cellValue = textValue;
                            }
                        }
                    }
                    
                    // cell.text가 없거나 빈 값인 경우
                    if (cellValue === '' || (cellValue !== undefined && cellValue !== null && cellValue.trim() === '')) {
                        // 하이퍼링크 속성이 있는 경우 우선 확인 (ExcelJS의 cell.hyperlink)
                        if (cell.hyperlink) {
                            if (typeof cell.hyperlink === 'string') {
                                cellValue = cell.hyperlink;
                            } else if (typeof cell.hyperlink === 'object') {
                                // cell.hyperlink가 객체인 경우
                                const hyperlinkObj = cell.hyperlink as any;
                                if (hyperlinkObj.hyperlink && typeof hyperlinkObj.hyperlink === 'string') {
                                    cellValue = hyperlinkObj.hyperlink;
                                } else if (hyperlinkObj.text && typeof hyperlinkObj.text === 'string') {
                                    cellValue = hyperlinkObj.text;
                                }
                            }
                        }
                        // cell.value 확인
                        else if (cell.value !== undefined && cell.value !== null) {
                            // 원시 타입인 경우
                            if (typeof cell.value !== 'object') {
                                cellValue = String(cell.value);
                            } 
                            // 객체인 경우
                            else {
                                // Date 객체
                                if (cell.value instanceof Date) {
                                    cellValue = cell.value.toLocaleString();
                                }
                                // Formula 객체의 result
                                else if ('result' in cell.value) {
                                    const result = (cell.value as any).result;
                                    if (result != null && typeof result !== 'object') {
                                        cellValue = String(result);
                                    }
                                }
                                // text 속성이 있는 경우
                                else if ('text' in cell.value) {
                                    const textValue = (cell.value as any).text;
                                    if (typeof textValue === 'string') {
                                        cellValue = textValue;
                                    } else if (textValue != null && typeof textValue !== 'object') {
                                        cellValue = String(textValue);
                                    }
                                }
                                // hyperlink 속성이 있는 경우 (하이퍼링크 객체)
                                else if ('hyperlink' in cell.value) {
                                    const hyperlinkObj = cell.value as any;
                                    // text 속성 우선
                                    if (hyperlinkObj.text && typeof hyperlinkObj.text === 'string') {
                                        cellValue = hyperlinkObj.text;
                                    } 
                                    // hyperlink 속성 사용
                                    else if (typeof hyperlinkObj.hyperlink === 'string') {
                                        cellValue = hyperlinkObj.hyperlink;
                                    }
                                }
                            }
                        }
                    }
                }
                
                // mailto: 접두사 제거 (이메일 주소만 추출)
                if (cellValue && typeof cellValue === 'string') {
                    cellValue = cellValue.replace(/^mailto:/i, '');
                }
                
                rowValues.push(cellValue);
            }
            jsonData.push(rowValues);
        });

        if (jsonData.length === 0) {
            throw new Error('Excel file is empty');
        }

        // 헤더 행을 기준으로 모든 컬럼 포함 (데이터가 없어도 헤더가 있으면 컬럼 유지)
        const parsedColumnCount = jsonData[0]?.length || 0;
        const validColumnIndices: number[] = [];
        const validColumns: string[] = [];
        
        // 헤더 행을 기준으로 모든 컬럼 포함 (인덱스 순서 유지)
        for (let colIndex = 0; colIndex < parsedColumnCount; colIndex++) {
            // 헤더 이름 추출
            const headerValue = jsonData[0][colIndex];
            let columnName = '';
            
            // 헤더가 있으면 컬럼 포함 (데이터가 없어도)
            if (headerValue !== undefined && headerValue !== null) {
                if (typeof headerValue === 'string') {
                    columnName = headerValue.trim() || `Column${colIndex + 1}`;
                } else if (typeof headerValue !== 'object') {
                    columnName = String(headerValue).trim() || `Column${colIndex + 1}`;
                } else {
                    columnName = `Column${colIndex + 1}`;
                }
                
                // 헤더가 있으면 컬럼 포함 (공백이어도 포함)
                if (columnName) {
                    validColumnIndices.push(colIndex);
                    validColumns.push(columnName);
                }
            }
        }

        // 헤더가 있는 컬럼이 없으면 에러
        if (validColumns.length === 0) {
            throw new Error('Excel file contains no header columns');
        }

        console.log('파일 파싱 - 유효한 컬럼 인덱스:', validColumnIndices);
        console.log('파일 파싱 - 유효한 컬럼명:', validColumns);
        console.log('파일 파싱 - 전체 데이터:', jsonData);

        // 유효한 컬럼만 포함하여 rows 생성
        const allRows = jsonData.map((row: any[]) => {
            const rowObj: any = {};
            validColumns.forEach((col, index) => {
                const originalColIndex = validColumnIndices[index];
                const value = row[originalColIndex];
                rowObj[col] = value !== undefined && value !== null ? String(value) : '';
            });
            return rowObj;
        });

        // 빈 행 필터링 (모든 컬럼의 값이 비어있는 행 제거)
        // 첫 번째 행은 헤더이므로 유지
        const headerRow = allRows[0];
        const dataRows = allRows.slice(1).filter((row: any) => {
            // 유효한 컬럼 중 하나라도 값이 있으면 유지
            return validColumns.some((col: string) => {
                const value = row[col];
                return value !== undefined && value !== null && String(value).trim() !== '';
            });
        });

        // 헤더와 데이터 행 결합
        const rows = [headerRow, ...dataRows];

        console.log('파일 파싱 - 파싱된 rows:', rows);
        console.log('파일 파싱 - rows 개수:', rows.length);
        console.log('파일 파싱 - 첫 번째 row (헤더):', rows[0]);
        console.log('파일 파싱 - 데이터 행 개수:', dataRows.length);
        console.log('파일 파싱 - columns:', validColumns);

        return { columns: validColumns, rows };
    } catch (error: any) {
        console.error('Excel 파일 파싱 실패:', error);
        throw new Error(`Failed to parse Excel file: ${error.message}`);
    }
};

// 파일을 File 객체로 정규화하는 헬퍼 함수
const normalizeFile = (input: File[] | FileList | File | null): File | null => {
    if (!input) return null;
    
    if (input instanceof File) {
        return input;
    }
    
    if (input instanceof FileList) {
        return input.length > 0 ? input[0] : null;
    }
    
    if (Array.isArray(input)) {
        return input.length > 0 ? input[0] : null;
    }
    
    return null;
};

// 파일 변경 핸들러
const handleFileChange = async (files: File[] | FileList | File | null) => {
    uploadError.value = '';
    
    const file = normalizeFile(files);
    
    if (!file || !file.name) {
        uploadError.value = 'Invalid file selected.';
        localFile.value = null;
        emit('update:file', null);
        return;
    }
    
    // 파일 형식 검증
    const fileName = file.name.toLowerCase();
    const isExcel = fileName.endsWith('.xlsx');
    const isZip = fileName.endsWith('.zip');
    
    if (!isExcel && !isZip) {
        uploadError.value = 'Only Excel files (.xlsx) or ZIP files (.zip) are allowed.';
        localFile.value = null;
        emit('update:file', null);
        return;
    }
    
    // ZIP 파일인 경우 내부 검증
    if (isZip) {
        const isValid = await validateZipFile(file);
        if (!isValid) {
            localFile.value = null;
            emit('update:file', null);
            return;
        }
    }
    
    localFile.value = file;
    emit('update:file', file);
    
    // Excel 파일인 경우 파싱
    if (isExcel) {
        try {
            const parsedData = await parseExcelFile(file);
            emit('file-uploaded', parsedData);
        } catch (error: any) {
            uploadError.value = error.message || 'Failed to parse Excel file';
            localFile.value = null;
            emit('update:file', null);
        }
    }
};

// 드래그앤드롭 핸들러
const handleDragOver = (e: DragEvent) => {
    e.preventDefault();
    isDragging.value = true;
};

const handleDragLeave = () => {
    isDragging.value = false;
};

const handleDrop = async (e: DragEvent) => {
    e.preventDefault();
    isDragging.value = false;
    
    const files = e.dataTransfer?.files;
    if (files && files.length > 0) {
        await handleFileChange(Array.from(files));
    }
};

watch(() => props.file, (newFile) => {
    localFile.value = newFile;
});
</script>

<template>
    <div class="file-upload-section">
        <!-- Contact Type 선택 및 샘플 파일 다운로드 -->
        <div class="d-flex gap-4 mb-6 align-start">
            <!-- 왼쪽: 라디오 옵션 -->
            <div class="contact-type-section flex-grow-1">
                <v-label class="mb-2 text-body-1">
                    Step 1: Select Contact Type to Upload <span class="text-error">*</span>
                </v-label>
                <v-radio-group v-model="localContactType" inline>
                    <v-radio label="Individual" value="Individual" />
                    <v-radio label="Corporate" value="Corporate" />
                </v-radio-group>
            </div>
            <!-- 오른쪽: 샘플 파일 다운로드 -->
            <v-card variant="outlined" class="pa-4 official-form-card">
                <h6 class="text-subtitle-1 mb-3">Official Form</h6>
                <div class="d-flex flex-column gap-2">
                    <v-btn
                        variant="text"
                        prepend-icon="mdi-microsoft-excel"
                        @click="generateIndividualTemplate"
                        :disabled="!localContactType || localContactType !== 'Individual'"
                        block
                        class="justify-start text-left"
                    >
                        Individua_contact_upload_forml.xlsx
                    </v-btn>
                    <v-btn
                        variant="text"
                        prepend-icon="mdi-microsoft-excel"
                        @click="generateCorporateTemplate"
                        :disabled="!localContactType || localContactType !== 'Corporate'"
                        block
                        class="justify-start text-left"
                    >
                        Corporate_upload_form.xlsx
                    </v-btn>
                </div>
            </v-card>
        </div>
        
        <!-- 파일 업로드 영역 -->
        <v-card
            variant="outlined"
            :class="{ 'drag-over': isDragging }"
            @dragover="handleDragOver"
            @dragleave="handleDragLeave"
            @drop="handleDrop"
            class="file-drop-zone pa-4"
        >
            <v-file-input
                :model-value="localFile ? [localFile] : []"
                label="Drag & drop your file, or click Upload"
                accept=".xlsx,.zip"
                variant="outlined"
                density="comfortable"
                prepend-icon=""
                @update:model-value="handleFileChange"
            />
            <p class="text-caption mt-2 text-medium-emphasis">
                Excel format only: .xlsx or .zip containing .xlsx files
            </p>
            <v-alert
                v-if="uploadError"
                type="error"
                density="compact"
                class="mt-2"
            >
                {{ uploadError }}
            </v-alert>
        </v-card>
    </div>
</template>

<style scoped lang="scss">
.file-upload-section {
    .contact-type-section {
        .v-label {
            font-weight: 500;
        }
        
        .v-radio-group {
            margin: 0;
        }
    }
    
    .official-form-card {
        min-width: 320px;
        max-width: 360px;
        flex-shrink: 0;
    }
    
    .file-drop-zone {
        transition: all 0.3s ease;
        border: 2px dashed rgba(var(--v-border-color), var(--v-border-opacity));
        
        &.drag-over {
            border-color: rgb(var(--v-theme-primary));
            background-color: rgba(var(--v-theme-primary), 0.05);
        }
    }
}
</style>

